import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { tournamentAPI, playerAPI } from '../services/api';
import './TournamentList.css';

const TournamentList = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [playerLinks, setPlayerLinks] = useState([]);
  const [expandedActions, setExpandedActions] = useState({});
  
  const itemsPerPage = 25;

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const response = await tournamentAPI.getAll();
      
      // Enhance tournaments with player counts (feedback counts now come from backend)
      const enhancedTournaments = await Promise.all(
        response.data.map(async (tournament) => {
          try {
            const playersResponse = await playerAPI.getAll(tournament._id);
            return {
              ...tournament,
              playerCount: playersResponse.data.length,
              feedbackCount: tournament.feedbackCount || 0,
              version: tournament.version || 1,
              lastModified: tournament.updatedAt || tournament.createdAt
            };
          } catch (err) {
            console.warn(`Failed to fetch players for tournament ${tournament._id}:`, err);
            return {
              ...tournament,
              playerCount: 0,
              feedbackCount: tournament.feedbackCount || 0,
              version: tournament.version || 1,
              lastModified: tournament.updatedAt || tournament.createdAt
            };
          }
        })
      );
      
      setTournaments(enhancedTournaments);
    } catch (err) {
      setError('Failed to load tournaments');
      console.error('Error fetching tournaments:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: '#6c757d', label: 'Draft' },
      setup: { color: '#fd7e14', label: 'Setup' },
      processing: { color: '#0dcaf0', label: 'Processing' },
      'published-for-feedback': { color: '#0d6efd', label: 'Published for Feedback' },
      completed: { color: '#198754', label: 'Completed' },
      finalized: { color: '#198754', label: 'Finalized' },
      published: { color: '#198754', label: 'Published' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    
    return (
      <span 
        className="status-badge"
        style={{ backgroundColor: config.color }}
      >
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatLastModified = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handlePublishForFeedback = async (tournamentId) => {
    try {
      if (window.confirm('Publish this tournament for player feedback?')) {
        await tournamentAPI.update(tournamentId, { 
          status: 'published-for-feedback',
          feedbackDeadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
        });
        fetchTournaments(); // Reload the list
        alert('Tournament published for feedback! Players can now access the public view.');
      }
    } catch (error) {
      console.error('Error publishing tournament:', error);
      alert('Failed to publish tournament. Please try again.');
    }
  };

  const handleViewPublicLink = async (tournament) => {
    try {
      setSelectedTournament(tournament);
      
      // Generate player links by fetching players
      const playersResponse = await playerAPI.getAll(tournament._id);
      const players = playersResponse.data;
      
      const baseUrl = window.location.origin;
      const publicUrl = `${baseUrl}/tournament/${tournament._id}/teesheet/view`;
      
      const links = players.map(player => ({
        name: player.name,
        ghin: player.ghin,
        personalizedUrl: `${publicUrl}?token=${player.ghin}`
      }));
      
      setPlayerLinks([
        {
          name: 'Public View (No Token)',
          ghin: 'public',
          personalizedUrl: publicUrl
        },
        ...links
      ]);
      
      setShowLinksModal(true);
    } catch (error) {
      console.error('Error generating player links:', error);
      alert('Failed to generate player links. Please try again.');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Link copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy link. Please copy manually.');
    });
  };

  const closeLinksModal = () => {
    setShowLinksModal(false);
    setSelectedTournament(null);
    setPlayerLinks([]);
  };

  const toggleActions = (tournamentId) => {
    setExpandedActions(prev => ({
      ...prev,
      [tournamentId]: !prev[tournamentId]
    }));
  };


  const filteredAndSortedTournaments = tournaments
    .filter(tournament => {
      if (statusFilter === 'all') return true;
      return tournament.status === statusFilter;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.date || a.createdAt);
          bValue = new Date(b.date || b.createdAt);
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'playerCount':
          aValue = a.playerCount;
          bValue = b.playerCount;
          break;
        case 'lastModified':
          aValue = new Date(a.lastModified);
          bValue = new Date(b.lastModified);
          break;
        default:
          aValue = a[sortBy];
          bValue = b[sortBy];
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const totalPages = Math.ceil(filteredAndSortedTournaments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTournaments = filteredAndSortedTournaments.slice(startIndex, startIndex + itemsPerPage);

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="tournament-list-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading tournaments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tournament-list-page">
        <div className="error-state">
          <h2>Error Loading Tournaments</h2>
          <p>{error}</p>
          <button onClick={fetchTournaments} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-list-page">
      <div className="back-navigation">
        <Link to="/dashboard" className="back-link">
          ← Back to Dashboard
        </Link>
      </div>
      <header className="tournament-header">
        <div className="header-content">
          <h1>Tournaments</h1>
          <p className="tournament-subtitle">Manage tournaments and tee time assignments</p>
        </div>
        <div className="header-actions">
          <Link to="/setup" className="create-button">
            + Create Tournament
          </Link>
        </div>
      </header>

      {/* Tournament Statistics */}
      <div className="tournament-stats">
        <div className="stat-card">
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <div className="stat-number">{tournaments.length}</div>
            <div className="stat-label">Total Tournaments</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⚡</div>
          <div className="stat-content">
            <div className="stat-number">{tournaments.filter(t => t.status === 'setup' || t.status === 'processing').length}</div>
            <div className="stat-label">Active Tournaments</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-number">{tournaments.filter(t => t.status === 'completed' || t.status === 'finalized').length}</div>
            <div className="stat-label">Completed Tournaments</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-number">{tournaments.reduce((total, t) => total + (t.playerCount || 0), 0)}</div>
            <div className="stat-label">Total Players</div>
          </div>
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>Filter by Status:</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="setup">Setup</option>
            <option value="processing">Processing</option>
            <option value="published-for-feedback">Published for Feedback</option>
            <option value="completed">Completed</option>
            <option value="finalized">Finalized</option>
          </select>
        </div>
        
        <div className="results-info">
          Showing {paginatedTournaments.length} of {filteredAndSortedTournaments.length} tournaments
        </div>
      </div>

      {filteredAndSortedTournaments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-content">
            <h2>No tournaments found</h2>
            <p>Create your first tournament to get started</p>
            <Link to="/setup" className="create-button">
              + Create Tournament
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div className="tournament-table-container">
            <table className="tournament-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="sortable">
                    Tournament Info {getSortIcon('name')}
                  </th>
                  <th onClick={() => handleSort('playerCount')} className="sortable">
                    Statistics {getSortIcon('playerCount')}
                  </th>
                  <th onClick={() => handleSort('status')} className="sortable">
                    Status & Progress {getSortIcon('status')}
                  </th>
                  <th onClick={() => handleSort('lastModified')} className="sortable">
                    Last Modified {getSortIcon('lastModified')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTournaments.map(tournament => (
                  <tr key={tournament._id}>
                    {/* Tournament Info Column */}
                    <td>
                      <div className="tournament-info">
                        <div className="tournament-title">
                          <h3>{tournament.name}</h3>
                          {tournament.tournamentType === 'team' && (
                            <span className="tournament-type-badge">Team</span>
                          )}
                        </div>
                        <div className="tournament-date">
                          📅 {formatDate(tournament.date)}
                        </div>
                      </div>
                    </td>

                    {/* Statistics Column */}
                    <td>
                      <div className="tournament-stats-cell">
                        <div className="stat-row">
                          <span className="stat-label">Players</span>
                          <span className="stat-value players">{tournament.playerCount}</span>
                        </div>
                        <div className="stat-row">
                          <span className="stat-label">Version</span>
                          <span className="stat-value version">v{tournament.version}</span>
                        </div>
                      </div>
                    </td>

                    {/* Status & Progress Column */}
                    <td>
                      <div className="tournament-status-cell">
                        {getStatusBadge(tournament.status)}
                        <div className="feedback-info">
                          {tournament.feedbackCount > 0 ? (
                            <>
                              <span className="feedback-badge">{tournament.feedbackCount}</span>
                              <span className="feedback-label">Feedback</span>
                            </>
                          ) : (
                            <span className="no-feedback">No feedback yet</span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Last Modified Column */}
                    <td>
                      <div className="tournament-modified-cell">
                        <div className="modified-time">{formatLastModified(tournament.lastModified)}</div>
                        <div className="modified-label">Last Updated</div>
                      </div>
                    </td>

                    {/* Actions Column with Expandable Icons */}
                    <td className="actions">
                      <div className="actions-container">
                        <button 
                          className={`actions-trigger-icon ${expandedActions[tournament._id] ? 'active' : ''}`}
                          onClick={() => toggleActions(tournament._id)}
                          title="Actions"
                        >
                          ⚙️
                        </button>
                        
                        <div className={`actions-expandable ${expandedActions[tournament._id] ? 'expanded' : ''}`}>
                          <Link 
                            to={`/editor/${tournament._id}`} 
                            className="action-icon-button edit"
                            title="Edit Tournament"
                          >
                            ✏️
                          </Link>
                          
                          {tournament.status === 'draft' && (
                            <button 
                              onClick={() => handlePublishForFeedback(tournament._id)}
                              className="action-icon-button publish"
                              title="Publish for Player Feedback"
                            >
                              🚀
                            </button>
                          )}
                          
                          {(tournament.status === 'published-for-feedback' || tournament.status === 'completed') && (
                            <>
                              <Link 
                                to={`/tournament/${tournament._id}/teesheet/view`} 
                                className="action-icon-button visit"
                                title="View Public Sheet"
                                target="_blank"
                              >
                                👁️
                              </Link>
                              <button 
                                onClick={() => handleViewPublicLink(tournament)}
                                className="action-icon-button links"
                                title="Get Public Link & Player Tokens"
                              >
                                🔗
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                Previous
              </button>
              
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Links Modal */}
      {showLinksModal && (
        <div className="modal-overlay" onClick={closeLinksModal}>
          <div className="links-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Public Links - {selectedTournament?.name}</h3>
              <button onClick={closeLinksModal} className="close-button">×</button>
            </div>
            
            <div className="modal-content">
              <p className="modal-description">
                Share these links with players to access the tournament tee sheet. 
                Each player gets a personalized view with their group highlighted.
              </p>
              
              <div className="links-list">
                {playerLinks.map((link, index) => (
                  <div key={index} className="link-item">
                    <div className="link-info">
                      <strong>{link.name}</strong>
                      {link.ghin !== 'public' && (
                        <span className="ghin-badge">GHIN: {link.ghin}</span>
                      )}
                    </div>
                    <div className="link-actions">
                      <input 
                        type="text" 
                        value={link.personalizedUrl} 
                        readOnly 
                        className="link-input"
                      />
                      <button 
                        onClick={() => copyToClipboard(link.personalizedUrl)}
                        className="copy-button"
                        title="Copy to clipboard"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="modal-footer">
                <p className="help-text">
                  💡 Players can also access their personalized view by adding ?token=THEIR_GHIN to the public URL
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentList;